/**
 * Flow optimization trigger for the Flow Designer's "Optimize" menu item.
 * Convergence forks the flow, iterates the fork's cache with cache-only runs
 * via the convergence agent, and on success promotes the proven cache back
 * to the origin flow (see
 * `apps/api/src/workflows/routes/workflow-convergence.routes.ts`).
 *
 * Live status while a run is in flight is no longer polled from a dedicated
 * status route — it rides along on the workflow detail payload's
 * `optimization` field (`GET /workflows/:id`), visible to every org member,
 * and is read directly off that query in `FlowDesigner.tsx`. This module
 * only owns kicking a run off.
 *
 * @module ui/pages/FlowDesigner/hooks/useCacheConvergence
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';

export type CacheConvergencePhase =
  | 'preflight'
  | 'fork'
  | 'converge'
  | 'verify'
  | 'promote'
  | 'origin_gate'
  | 'finalize';

/** Shape of the `optimization` field on a workflow detail response, non-null only while a run is active. */
export type ActiveOptimization = {
  convergenceRunId: string;
  status: 'queued' | 'running' | 'waiting_for_feedback';
  phase: CacheConvergencePhase | null;
  cycleCount: number;
  cycleCap: number | null;
  startedAt: string | null;
  /** Set only while `status === 'waiting_for_feedback'` — the pending `maintenance_feedback_requests` row. */
  feedbackRequestId: string | null;
} | null;

/** Kicks off an optimization run for the workflow (POST /workflows/:id/converge). */
export function useCacheConvergenceMutation(workflowId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/converge`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ confirmSideEffects: true }),
      });
      if (!r.ok) throw await toApiError(r, 'Failed to start optimization');
      return r.json() as Promise<{ ok: true; data: { convergenceRunId: string; jobId: string } }>;
    },
    onSuccess: () => {
      // The workflow detail query carries `optimization` — invalidate it so
      // the "in-flight" banner appears immediately rather than waiting for
      // its next scheduled refetch.
      qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
    },
  });
}
